import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router';

import { apiPost } from '../Api';
import './Auth.css';
import PublicShell from './PublicShell';
import { hasErrors, validateLogin } from './authValidation';

export default function Login({ handleLogin }) {
    const [values, setValues] = useState({ username: '', password: '' });
    const [errors, setErrors] = useState({ username: '', password: '' });
    const [status, setStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRequestingReset, setIsRequestingReset] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [redirectToHome, setRedirectToHome] = useState(false);
    const fieldRefs = useRef({});

    useEffect(() => {
        if (localStorage.getItem('token')) {
            setRedirectToHome(true);
        }
    }, []);

    const focusFirstError = (nextErrors) => {
        const field = ['username', 'password'].find((name) => nextErrors[name]);
        if (field) fieldRefs.current[field]?.focus();
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        const nextValues = { ...values, [name]: value };
        setValues(nextValues);
        setStatus(null);

        if (errors[name]) {
            setErrors((current) => ({
                ...current,
                [name]: validateLogin(nextValues)[name],
            }));
        }
    };

    const handleBlur = (event) => {
        const nextErrors = validateLogin(values);
        setErrors((current) => ({
            ...current,
            [event.target.name]: nextErrors[event.target.name],
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const nextErrors = validateLogin(values);
        setErrors(nextErrors);
        setStatus(null);
        if (hasErrors(nextErrors)) {
            focusFirstError(nextErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('username', values.username.trim());
            formData.append('password', values.password);

            const response = await apiPost('/user/login', formData, false);
            const result = await response.json();

            if (!result.success) {
                const accountMessage = /banned|not allowed|pts/i.test(result.message || '')
                    ? result.message
                    : 'Username or password is incorrect.';
                setStatus({ type: 'error', text: accountMessage });
                fieldRefs.current.password?.focus();
                return;
            }

            localStorage.setItem('token', result.access_token);
            handleLogin(result.access_token);
            setRedirectToHome(true);
        } catch {
            setStatus({
                type: 'error',
                text: 'We could not sign you in. Check your connection and try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPassword = async () => {
        const usernameError = validateLogin({ ...values, password: 'present' }).username;
        if (usernameError) {
            const nextErrors = { ...errors, username: usernameError };
            setErrors(nextErrors);
            fieldRefs.current.username?.focus();
            return;
        }

        setIsRequestingReset(true);
        setStatus(null);
        try {
            const formData = new FormData();
            formData.append('username', values.username.trim());
            await apiPost('/user/ForgotPasswordRequest', formData, false);
            setStatus({
                type: 'success',
                text: 'If an account matches that username, password instructions have been sent.',
            });
        } catch {
            setStatus({
                type: 'error',
                text: 'We could not request password instructions. Please try again.',
            });
        } finally {
            setIsRequestingReset(false);
        }
    };

    if (redirectToHome) {
        return <Navigate to="/" />;
    }

    return (
        <PublicShell compact>
            <section className="auth-card" aria-labelledby="login-title">
                <p className="auth-card__eyebrow">Welcome back, operative</p>
                <h1 id="login-title">Log in</h1>
                <p className="auth-card__intro">
                    Continue where you left off. The wasteland probably did not improve.
                </p>

                <form className="auth-form" onSubmit={handleSubmit} noValidate>
                    {status && (
                        <p
                            className={`auth-status auth-status--${status.type}`}
                            role={status.type === 'error' ? 'alert' : 'status'}
                        >
                            {status.text}
                        </p>
                    )}

                    <div className="auth-field">
                        <label htmlFor="login-username">Username</label>
                        <input
                            ref={(element) => { fieldRefs.current.username = element; }}
                            id="login-username"
                            name="username"
                            type="text"
                            value={values.username}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck="false"
                            aria-invalid={Boolean(errors.username)}
                            aria-describedby={errors.username ? 'login-username-error' : undefined}
                        />
                        {errors.username && (
                            <p id="login-username-error" className="auth-field__error">
                                {errors.username}
                            </p>
                        )}
                    </div>

                    <div className="auth-field">
                        <label htmlFor="login-password">Password</label>
                        <div className="auth-field__input-wrap">
                            <input
                                ref={(element) => { fieldRefs.current.password = element; }}
                                id="login-password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                autoComplete="current-password"
                                aria-invalid={Boolean(errors.password)}
                                aria-describedby={errors.password ? 'login-password-error' : undefined}
                            />
                            <button
                                className="auth-password-toggle"
                                type="button"
                                aria-pressed={showPassword}
                                onClick={() => setShowPassword((current) => !current)}
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                        {errors.password && (
                            <p id="login-password-error" className="auth-field__error">
                                {errors.password}
                            </p>
                        )}
                        <button
                            className="auth-text-button"
                            type="button"
                            onClick={handleForgotPassword}
                            disabled={isRequestingReset}
                        >
                            {isRequestingReset ? 'Requesting instructions…' : 'Forgot your password?'}
                        </button>
                    </div>

                    <button
                        className="auth-button"
                        type="submit"
                        disabled={isSubmitting}
                        aria-busy={isSubmitting}
                    >
                        {isSubmitting ? 'Logging in…' : 'Log in'}
                    </button>
                </form>

                <p className="auth-card__alternate">
                    New to Progress Lands? <Link to="/register">Pre-register</Link>
                </p>
            </section>
        </PublicShell>
    );
}
